import { NotificationChannel } from '@frontend/common';
import {
    type HtmlEditorMode,
    type HtmlSourceValidationCode,
    type HtmlSourceValidationMessage,
    buildPreviewDocument,
    clearHtmlTemplateDraft,
    formatHtmlSource,
    htmlTemplateDraftKey,
    isFullHtmlDocument,
    readHtmlTemplateDraft,
    validateHtmlSource,
    validatePlainText,
    writeHtmlTemplateDraft
} from '@frontend/html-template-editor';
import { NotificationContentClient } from '@frontend/notification/api';
import { ToastUtil } from '@frontend/toast-utils';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { NotificationContentEditorProps } from './editor.component';

export type EditorMode = HtmlEditorMode;
export type SourceValidationMessageCode = HtmlSourceValidationCode;
export type SourceValidationMessage = HtmlSourceValidationMessage;

interface EditorDraft {
    subject: string;
    body: string;
    timestamp: number;
}

const getDraftKey = (contentId: string): string => htmlTemplateDraftKey('notification-content-editor', contentId);

const stripHtml = (source: string): string => {
    const document = new DOMParser().parseFromString(source, 'text/html');
    return document.body.textContent ?? source;
};

export { buildPreviewDocument, formatHtmlSource, isFullHtmlDocument, validatePlainText };

export const validateSource = (source: string, placeholders: string[] = [], section?: string): SourceValidationMessage[] =>
    validateHtmlSource(source, {
        section,
        placeholders,
        includeUnsafeMarkup: true,
        includeImages: true,
        includePlaceholders: true,
        includeEmailCompatibility: true
    });

export const useEditor = (props: NotificationContentEditorProps) => {
    const intl = useIntl();
    const isRichContent = props.content.channel === NotificationChannel.EMAIL;
    const initialSubject = props.content.subject ?? '';
    const initialBody = isRichContent ? (props.content.body ?? '') : stripHtml(props.content.body ?? '');
    const [subject, setSubject] = useState<string>(initialSubject);
    const [body, setBody] = useState<string>(initialBody);
    const [baseline, setBaseline] = useState({ subject: initialSubject, body: initialBody });
    const [mode, setMode] = useState<EditorMode>('visual');
    const [editorKey, setEditorKey] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [recoverableDraft, setRecoverableDraft] = useState<EditorDraft | null>(null);

    const bodySection = intl.formatMessage({
        id: 'content-detail.body',
        description: 'Label above the notification content body editor',
        defaultMessage: 'Body'
    });

    const clearDraft = () => {
        clearHtmlTemplateDraft(getDraftKey(props.content.id));
        setRecoverableDraft(null);
    };

    useEffect(() => {
        const nextSubject = props.content.subject ?? '';
        const nextBody = isRichContent ? (props.content.body ?? '') : stripHtml(props.content.body ?? '');
        const draft = readHtmlTemplateDraft<EditorDraft>(getDraftKey(props.content.id));

        setSubject(nextSubject);
        setBody(nextBody);
        setBaseline({ subject: nextSubject, body: nextBody });
        setMode('visual');
        setRecoverableDraft(draft && (draft.subject !== nextSubject || draft.body !== nextBody) ? draft : null);
        setEditorKey((current) => current + 1);
    }, [isRichContent, props.content.id, props.content.subject, props.content.body]);

    const validationMessages = useMemo(() => {
        if (!isRichContent) return validatePlainText(body, props.placeholders, bodySection);
        return validateSource(body, props.placeholders, bodySection);
    }, [body, bodySection, isRichContent, props.placeholders]);
    const validationErrors = validationMessages.filter((message) => message.level === 'error');
    const isDirty = subject !== baseline.subject || body !== baseline.body;

    useEffect(() => {
        if (!isDirty) return;

        writeHtmlTemplateDraft(getDraftKey(props.content.id), {
            subject,
            body,
            timestamp: Date.now()
        });
    }, [body, isDirty, props.content.id, subject]);

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (!isDirty) return;
            event.preventDefault();
            event.returnValue = '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);

    const changeMode = (nextMode: EditorMode) => {
        if (!isRichContent) return;
        setMode(nextMode);
        setEditorKey((current) => current + 1);
    };

    const resetContent = () => {
        setSubject(baseline.subject);
        setBody(baseline.body);
        setMode('visual');
        setEditorKey((current) => current + 1);
        clearDraft();
        ToastUtil.success(
            <FormattedMessage
                id='content-detail.notification-content-reset'
                description='Toast shown after resetting notification content editor changes'
                defaultMessage='Notification content reset successfully'
            />
        );
    };

    const formatSource = () => {
        setBody(formatHtmlSource(body));
    };

    const save = async () => {
        if (validationErrors.length > 0 || isSaving) return;

        setIsSaving(true);
        try {
            await NotificationContentClient.patchNotificationContent(props.content.account_id, props.content.template_id, props.content.id, {
                body,
                subject
            });
            setBaseline({ subject, body });
            clearDraft();
            ToastUtil.success(
                <FormattedMessage
                    id='content-detail.notification-content-saved'
                    description='Toast shown after saving notification content'
                    defaultMessage='Notification content saved successfully'
                />
            );
        } catch {
            ToastUtil.error(
                <FormattedMessage
                    id='content-detail.notification-content-save-error'
                    description='Toast shown when saving notification content fails'
                    defaultMessage='Error saving notification content'
                />
            );
        } finally {
            setIsSaving(false);
        }
    };

    const discardChanges = () => {
        if (
            !isDirty ||
            window.confirm(
                intl.formatMessage({
                    id: 'content-detail.editor.discard-confirmation',
                    description: 'Confirmation shown when leaving the notification content editor with unsaved changes',
                    defaultMessage: 'Discard unsaved changes?'
                })
            )
        ) {
            props.onCancel();
        }
    };

    const restoreDraft = () => {
        if (!recoverableDraft) return;
        setSubject(recoverableDraft.subject);
        setBody(recoverableDraft.body);
        setMode('visual');
        setRecoverableDraft(null);
        setEditorKey((current) => current + 1);
    };

    const discardDraft = () => clearDraft();

    return {
        subject,
        setSubject,
        body,
        setBody,
        mode,
        changeMode,
        editorKey,
        resetContent,
        formatSource,
        save,
        discardChanges,
        recoverableDraft,
        restoreDraft,
        discardDraft,
        isDirty,
        isSaving,
        validationMessages,
        validationErrors
    };
};
